import React, { useState, useEffect, useCallback } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API_URL } from "@/lib/api";
import { formatDateToYMD } from "@/lib/date";

const API = API_URL;

const CURRENCY_PAIRS = [
  "EUR/USD",
  "GBP/USD",
  "USD/JPY",
  "USD/CHF",
  "AUD/USD",
  "NZD/USD",
  "USD/CAD",
  "EUR/GBP",
  "EUR/JPY",
  "GBP/JPY",
  "AUD/JPY",
  "EUR/AUD",
  "GBP/AUD",
  "XAU/USD",
  "BTC/USD",
  "ETH/USD",
  "US30",
  "NASDAQ",
  "SPX500",
  "US100",
];

const STRATEGIES_LIST = [
  "Scalping",
  "Day Trading",
  "Swing Trading",
  "Position Trading",
  "Breakout",
  "Mean Reversion",
  "Momentum",
  "Grid Trading",
  "Arbitrage",
  "Other",
];

const INITIAL_FORM = {
  currency_pair: "",
  strategy: "",
  risk_amount: "",
  profit_loss: "",
  execution_date: formatDateToYMD(new Date()),
  notes: "",
  setup: "",
  grade: "",
  emotion: "",
};

export default function QuickTradeEntry({ currentStrategy, mode, onTradeSaved }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});

  // Keyboard shortcut: Cmd+Shift+T / Ctrl+Shift+T
  useEffect(() => {
    const down = (e) => {
      if (e.key === "T" && (e.metaKey || e.ctrlKey) && e.shiftKey) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", down);
    return () => document.removeEventListener("keydown", down);
  }, []);

  // Pre-fill date when opening
  useEffect(() => {
    if (open) {
      setForm((prev) => ({ ...prev, execution_date: formatDateToYMD(new Date()) }));
      setErrors({});
    }
  }, [open]);

  const validate = useCallback(() => {
    const newErrors = {};
    if (!form.currency_pair) newErrors.currency_pair = "Currency pair is required";
    if (!form.risk_amount || parseFloat(form.risk_amount) <= 0)
      newErrors.risk_amount = "Risk amount must be > 0";
    if (!form.execution_date) newErrors.execution_date = "Date is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    if (!currentStrategy) {
      toast.error("No strategy selected. Create a strategy first.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const tradeData = {
        currency_pair: form.currency_pair,
        strategy_id: currentStrategy.id,
        risk_amount: parseFloat(form.risk_amount),
        profit_loss: parseFloat(form.profit_loss) || 0,
        execution_date: form.execution_date,
        notes: form.notes || "",
        attachments: [],
        setup: form.setup ? form.setup.trim() : null,
        grade: form.grade ? parseInt(form.grade, 10) : null,
        emotion: form.emotion ? parseInt(form.emotion, 10) : null,
      };

      await axios.post(`${API}/trades?mode=${mode}`, tradeData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("Trade recorded successfully!");
      setForm(INITIAL_FORM);
      setOpen(false);
      if (onTradeSaved) onTradeSaved();
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving trade");
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  return (
    <>
      {/* Floating Action Button */}
      <div className="fixed bottom-6 right-6 z-50 md:bottom-8 md:right-8">
        <Button
          onClick={() => setOpen(true)}
          size="lg"
          className="h-14 w-14 rounded-full shadow-xl bg-gradient-to-br from-[#1877F2] to-blue-700 hover:from-blue-700 hover:to-[#1877F2] text-white transition-all duration-300 hover:scale-105 hover:shadow-2xl active:scale-95 group"
          data-testid="quick-trade-fab"
          title="Quick Trade Entry (Cmd+Shift+T)"
        >
          <Plus className="h-6 w-6 transition-transform group-hover:rotate-90 duration-300" />
        </Button>
      </div>

      {/* Slide-out Panel */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
          <SheetHeader className="p-6 pb-4 border-b border-border">
            <SheetTitle className="text-xl font-bold flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-[#1877F2]/15 flex items-center justify-center">
                <Plus className="h-4 w-4 text-[#1877F2]" />
              </div>
              Quick Trade Entry
            </SheetTitle>
            <SheetDescription>
              Record a new trade in seconds.{" "}
              <kbd className="px-1.5 py-0.5 text-[10px] font-mono bg-muted rounded border text-muted-foreground">
                {navigator.platform?.includes("Mac") ? "⌘" : "Ctrl"}+Shift+T
              </kbd>
            </SheetDescription>
          </SheetHeader>

          <form
            onSubmit={handleSubmit}
            className="flex-1 overflow-y-auto p-6 space-y-5"
          >
            {/* Currency Pair */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Currency Pair <span className="text-destructive">*</span>
              </Label>
              <Select
                value={form.currency_pair}
                onValueChange={(val) => updateField("currency_pair", val)}
              >
                <SelectTrigger
                  className={`h-11 ${errors.currency_pair ? "border-destructive" : ""}`}
                  data-testid="quick-pair-select"
                >
                  <SelectValue placeholder="Select pair..." />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCY_PAIRS.map((pair) => (
                    <SelectItem key={pair} value={pair}>
                      {pair}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.currency_pair && (
                <p className="text-xs text-destructive">{errors.currency_pair}</p>
              )}
            </div>

            {/* Strategy */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Strategy</Label>
              <Select
                value={form.strategy}
                onValueChange={(val) => updateField("strategy", val)}
              >
                <SelectTrigger className="h-11" data-testid="quick-strategy-select">
                  <SelectValue placeholder="Select strategy type..." />
                </SelectTrigger>
                <SelectContent>
                  {STRATEGIES_LIST.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Risk & P/L */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">
                  Risk Amount <span className="text-destructive">*</span>
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="100.00"
                  value={form.risk_amount}
                  onChange={(e) => updateField("risk_amount", e.target.value)}
                  className={`h-11 ${errors.risk_amount ? "border-destructive" : ""}`}
                  data-testid="quick-risk-input"
                />
                {errors.risk_amount && (
                  <p className="text-xs text-destructive">{errors.risk_amount}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">P/L</Label>
                <Input
                  type="number"
                  step="0.01"
                  placeholder="50.00 or -50.00"
                  value={form.profit_loss}
                  onChange={(e) => updateField("profit_loss", e.target.value)}
                  className="h-11"
                  data-testid="quick-pnl-input"
                />
              </div>
            </div>

            {/* Date */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">
                Date <span className="text-destructive">*</span>
              </Label>
              <Input
                type="date"
                value={form.execution_date}
                onChange={(e) => updateField("execution_date", e.target.value)}
                className={`h-11 ${errors.execution_date ? "border-destructive" : ""}`}
                data-testid="quick-date-input"
              />
              {errors.execution_date && (
                <p className="text-xs text-destructive">{errors.execution_date}</p>
              )}
            </div>

            {/* Setup, grade, emotion */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Setup</Label>
              <Input
                list="quick-setup-options"
                placeholder="e.g. breakout"
                value={form.setup}
                onChange={(e) => updateField("setup", e.target.value)}
                className="h-11"
                data-testid="quick-setup-input"
              />
              <datalist id="quick-setup-options">
                {(currentStrategy?.setups || []).map((setup) => (
                  <option key={setup} value={setup} />
                ))}
              </datalist>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Execution grade</Label>
                <Select
                  value={form.grade}
                  onValueChange={(val) => updateField("grade", val)}
                >
                  <SelectTrigger className="h-11" data-testid="quick-grade-select">
                    <SelectValue placeholder="1-5" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((score) => (
                      <SelectItem key={score} value={`${score}`}>
                        {score}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Emotion</Label>
                <Select
                  value={form.emotion}
                  onValueChange={(val) => updateField("emotion", val)}
                >
                  <SelectTrigger className="h-11" data-testid="quick-emotion-select">
                    <SelectValue placeholder="1-5" />
                  </SelectTrigger>
                  <SelectContent>
                    {[1, 2, 3, 4, 5].map((score) => (
                      <SelectItem key={score} value={`${score}`}>
                        {score}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">Notes</Label>
              <Textarea
                placeholder="Optional trade notes..."
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                rows={3}
                className="resize-none text-sm"
                data-testid="quick-notes-input"
              />
            </div>

            {/* Strategy Indicator */}
            {currentStrategy && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-[#1877F2]/10 border border-[#1877F2]/20">
                <div className="h-6 w-6 rounded-md bg-[#1877F2] flex items-center justify-center">
                  <span className="text-[10px] font-bold text-white">
                    {currentStrategy.name?.charAt(0)}
                  </span>
                </div>
                <div className="text-xs">
                  <span className="text-muted-foreground">Saving to:</span>{" "}
                  <span className="font-semibold text-foreground">
                    {currentStrategy.name}
                  </span>
                </div>
              </div>
            )}
          </form>

          {/* Submit Button */}
          <div className="p-6 pt-0 border-t border-border mt-auto">
            <Button
              type="submit"
              onClick={handleSubmit}
              className="w-full h-12 rounded-full font-semibold bg-gradient-to-r from-[#1877F2] to-blue-600 hover:from-blue-600 hover:to-[#1877F2] text-white shadow-lg hover:shadow-xl transition-all"
              disabled={loading}
              data-testid="quick-trade-submit"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Saving...</span>
                </div>
              ) : (
                "Record Trade"
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
